import React, { useEffect, useState } from "react";
import { isNative } from "../lib/native";
import {
  DeviceMobile,
  X,
  ShareNetwork,
  PlusSquare,
  AppleLogo,
  AndroidLogo,
  DotsThreeVertical,
  CheckCircle,
} from "@phosphor-icons/react";

/**
 * Install scroll nudge.
 *
 * A small bottom-pinned toast that surfaces once for first-time mobile
 * visitors who haven't installed Zcardon. Fires on the *earlier* of two
 * triggers:
 *   1. The visitor scrolls past 800 px (typically past the hero)
 *   2. 30 seconds pass with the tab still focused
 *
 * Why: `DownloadAppButton` sits in the Landing hero — if a visitor scrolls
 * past it without tapping, they never see the install path again on that
 * page. `IosInstallPrompt` already covers iOS Safari users on landing, but
 * Android users had no equivalent. This component closes that gap.
 *
 * Renders nothing when:
 *   • already running in standalone display-mode (installed PWA)
 *   • running inside the Capacitor native shell
 *   • inside an in-app webview (FB / IG / Line / Twitter / TikTok) — those
 *     can't install
 *   • on a non-touch device (`maxTouchPoints < 1`) — desktop visitors get
 *     the QR fallback from `DownloadAppButton` instead
 *   • dismissed previously (sticky via localStorage)
 */
const STORAGE_KEY = "zcardon_install_nudge_dismissed";

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iosUa = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = /Mac/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return iosUa || iPadOS;
}
function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return Boolean(window.navigator.standalone) || window.matchMedia("(display-mode: standalone)").matches;
}
function isWebview() {
  if (typeof navigator === "undefined") return false;
  return /(FBAN|FBAV|Instagram|Line|Twitter|TikTok|MicroMessenger)/i.test(navigator.userAgent || "");
}

export default function InstallScrollNudge() {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false); // mini-modal with platform steps
  const [installEvent, setInstallEvent] = useState(null);
  const [justInstalled, setJustInstalled] = useState(false);

  // Capture Chrome's beforeinstallprompt so Android tap → one-tap install.
  useEffect(() => {
    const onBefore = (e) => { e.preventDefault(); setInstallEvent(e); };
    const onInstalled = () => {
      setJustInstalled(true);
      setInstallEvent(null);
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
      setTimeout(() => { setShow(false); setOpen(false); }, 2000);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Trigger logic. Bail out fast for ineligible platforms.
  useEffect(() => {
    if (isNative() || isStandalone() || isWebview()) return undefined;
    if (!isIos() && !isAndroid()) return undefined;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return undefined;
      // On iOS, IosInstallPrompt already covers the install path with its
      // own bottom-sheet (pinned at the same bottom-4 region). Wait until
      // that sheet has been dismissed before nudging again — otherwise the
      // two overlap and the nudge's CTA is unclickable. iOS users who
      // dismissed without installing get this as a polite second-chance.
      if (isIos() && localStorage.getItem("zcardon_ios_install_dismissed") !== "1") {
        return undefined;
      }
    } catch { return undefined; }

    let scrollTriggered = false;
    let timerTriggered = false;
    const onScroll = () => {
      if (!scrollTriggered && window.scrollY > 800) {
        scrollTriggered = true;
        setShow(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const timer = setTimeout(() => {
      if (!scrollTriggered && !timerTriggered) {
        timerTriggered = true;
        setShow(true);
      }
    }, 30000);

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    setShow(false);
    setOpen(false);
  };

  const handlePrimary = async () => {
    if (isAndroid() && installEvent) {
      // One-tap install — Chrome captured event is ready
      try {
        await installEvent.prompt();
        // Don't dismiss permanently — the appinstalled listener handles it.
      } catch {
        setOpen(true);
      }
    } else {
      // iOS or Android-without-event → show step-by-step modal
      setOpen(true);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="fixed bottom-4 inset-x-3 sm:left-auto sm:right-4 sm:bottom-6 sm:max-w-sm z-[55] rounded-2xl bg-[#112318] text-[#FBF9F6] shadow-2xl border border-white/10 animate-in slide-in-from-bottom-4"
        data-testid="install-scroll-nudge"
      >
        <div className="flex items-start gap-3 p-3.5">
          <span className="shrink-0 w-10 h-10 rounded-xl bg-[#184A30] flex items-center justify-center">
            <DeviceMobile size={20} weight="duotone" className="text-[#FBF9F6]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm leading-tight">Install Zcardon</p>
            <p className="text-[11px] text-white/65 leading-snug mt-0.5">
              {isIos()
                ? "Adds a home-screen icon. No App Store, no download."
                : "One tap. Adds a home-screen icon, runs full-screen."}
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install nudge"
            className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors text-white/60 hover:text-white"
            data-testid="install-scroll-nudge-dismiss"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <button
          type="button"
          onClick={handlePrimary}
          className="block w-full text-center bg-[#D96C4A] hover:bg-[#B04E2F] text-white text-sm font-semibold py-2.5 rounded-b-2xl transition-colors"
          data-testid="install-scroll-nudge-cta"
        >
          {isAndroid() && installEvent ? "Install now" : "Show me how"}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="install-scroll-nudge-modal"
        >
          <div className="bg-[#FBF9F6] rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between p-5 border-b border-[#E5E0D8]">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
                  {isIos() ? <AppleLogo size={14} weight="duotone" /> : <AndroidLogo size={14} weight="duotone" />}
                  {isIos() ? "iPhone · iPad" : "Android"}
                </div>
                <h2 className="serif text-2xl mt-1">Install Zcardon</h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="p-2 -m-2 text-[#4A5C50] hover:text-[#112318]"
                data-testid="install-scroll-nudge-modal-close"
              >
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {justInstalled ? (
                <div className="flex items-center gap-3 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <CheckCircle size={28} weight="fill" />
                  <p className="text-sm">
                    Installed — find Zcardon on your home screen.
                  </p>
                </div>
              ) : isIos() ? (
                <ol className="space-y-3 text-sm text-[#112318]">
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center text-xs font-semibold">1</span>
                    <span className="leading-relaxed">
                      Tap the <span className="font-semibold">Share</span> button{" "}
                      <ShareNetwork size={16} weight="duotone" className="inline align-text-bottom" />{" "}
                      at the bottom of Safari.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center text-xs font-semibold">2</span>
                    <span className="leading-relaxed">
                      Scroll down and tap{" "}
                      <span className="font-semibold">Add to Home Screen</span>{" "}
                      <PlusSquare size={16} weight="duotone" className="inline align-text-bottom" />.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center text-xs font-semibold">3</span>
                    <span className="leading-relaxed">
                      Tap <span className="font-semibold">Add</span>. The Zcardon
                      icon appears next to your other apps.
                    </span>
                  </li>
                </ol>
              ) : (
                <ol className="space-y-3 text-sm text-[#112318]">
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center text-xs font-semibold">1</span>
                    <span className="leading-relaxed">
                      Tap the{" "}
                      <DotsThreeVertical size={16} weight="bold" className="inline align-text-bottom" />{" "}
                      menu in the top-right of Chrome.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center text-xs font-semibold">2</span>
                    <span className="leading-relaxed">
                      Tap <span className="font-semibold">Install app</span>{" "}
                      (or <span className="font-semibold">Add to Home screen</span>).
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center text-xs font-semibold">3</span>
                    <span className="leading-relaxed">
                      Confirm <span className="font-semibold">Install</span>.
                      Zcardon opens straight from your home screen.
                    </span>
                  </li>
                </ol>
              )}
              <p className="text-[11px] text-[#4A5C50] leading-relaxed pt-1">
                Works fully offline. No App Store account required. Uninstall
                any time by long-pressing the icon.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
